import {
    ChangeDetectionStrategy, ChangeDetectorRef,
    Component,
    ComponentFactoryResolver,
    ComponentRef,
    EventEmitter, HostListener,
    Input, OnDestroy, OnInit,
    Output,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation
} from '@angular/core';
import { animationFrameScheduler, fromEvent, Subject, Subscription } from 'rxjs';
import { auditTime, takeUntil } from 'rxjs/operators';
import { SidePanelService } from 'src/app/shared/services/side-panel.service';
import { ProfilePolygonService } from 'src/app/shared/services/profile-polygon.service';
import componentsMapping, { ISidePanel, SidePanels } from '../../shared/services/side-panel.helper';
import {DataPointsServiceState} from '../../shared/services/datapoints-service-state';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {MUNICHRE_ARRAY, addressProfileDropdownMenuList, MUNICHRE_REPORT_ARRAY, PRECISELY_BUILDING_ARRAY, PRECISELY_CRIME_ARRAY, PRECISELY_WILDFIRE_RISK_ARRAY} from 'src/app/model/datapoint/location-profile/location-profile';
// import { TENSORFLIGHT_ARRAY }
import { UserStateService } from 'src/app/auth/user-state-service';
import { DatapointsPageStateService } from 'src/app/dataset/datapoints/datapoints-page-state.service';
import { isUndefined } from '../utils/util-master';
import { AccountService } from 'src/app/data-access-layer/account/account.service';
import { TessadataService } from 'src/app/data-access-layer/tessadata.service';
import { ActivatedRoute } from '@angular/router';
import { WorkspaceItemService } from 'src/app/data-access-layer/workspace-item/workspace-item.service';
import { DatapointsService } from 'src/app/data-access-layer/datapoints/datapoints.service';
import { COMMON_PANEL_WIDTH } from 'src/app/constants';

@Component({
    selector: 'app-side-panel',
    templateUrl: './side-panel.component.html',
    styleUrls: ['./side-panel.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidePanelComponent implements OnInit, OnDestroy, ISidePanel {
    @Input() id: string;
    @Input() size: string;
    @Input() width: number;
    @Input() panelTitle: string;
    @Input() panelIcon: string;
    @Input() backdrop: boolean;
    @Input() resizeable: boolean;
    @Input() component: SidePanels;
    @Input() componentConfigs: any;
    @Input() zIndex: number;
    @Output() showChange = new EventEmitter<boolean>();
    @Output() closed = new EventEmitter();

    isOpen: boolean;
    show: boolean;
    hasAccess: any;

    // Resize properties
    grabber = false;
    dragWidth: number;
    oldX = 0;
    private readonly PANEL_MARGIN = 75;
    baseWidth: number = COMMON_PANEL_WIDTH;
    minWidthPx: number = Math.round(COMMON_PANEL_WIDTH * 0.5);
    maxWidthPx: number = window.innerWidth - this.PANEL_MARGIN;
    subscription = new Subscription();
    closeModal$: Subject<boolean>;
    hideModal$: Subject<SidePanelComponent> = new Subject();
    onPanelFirst$: Subject<SidePanelComponent> = new Subject<SidePanelComponent>();
    destroy$: Subject<boolean>;
    componentRef: ComponentRef<any>;
    dropdownValueChanged$: Subject<string> = new Subject<string>();

    @ViewChild('container', { read: ViewContainerRef, static: true }) container: ViewContainerRef;

    selectedGroup: UntypedFormGroup;
    selectedValue = new UntypedFormControl('EXTERNAL');
    menuList: any;
    readonly ADDRESS_LOCATION_PROFILE = 'addressLocationProfilePanel';

    constructor(
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly userStateService: UserStateService,
        private readonly datapointsPageStateService: DatapointsPageStateService,
        private readonly componentFactoryResolver: ComponentFactoryResolver, private readonly dataPointServiceState: DataPointsServiceState, private fb: UntypedFormBuilder,
        private readonly accountService: AccountService,
        private readonly tessadataService: TessadataService,
        private readonly route: ActivatedRoute,
        public dataPointsServiceState: DataPointsServiceState,
        private readonly workspaceItemService: WorkspaceItemService,
        private readonly dpService: DatapointsService,
        private readonly sidePanelService: SidePanelService,
        private readonly profilePolygonService: ProfilePolygonService,
        ) {
        this.resizeable = false;
        this.closeModal$ = new Subject();
        this.destroy$ = new Subject();
    }

    getPanelIcon() {
        if (this.isFontAwesomeIcon()) {
            return 'fa ' + this.panelIcon;
        } else {
            return '../../../assets/icons/newIcons/panelIcons/' + this.panelIcon + ".svg";
        }
    }

    isFontAwesomeIcon() {
        return this.panelIcon.startsWith('fa-');
    }

    ngOnInit(): void {
        if (this.component) {
            this.selectedGroup = this.fb.group({
                defaultSelected: ['EXTERNAL']
            });
            let accountId = this.route.snapshot.paramMap.get('accountId');
            let datasetId = this.route.snapshot.paramMap.get('datasetId');
            this.dpService.getAddressProfileDropdown(accountId, datasetId).subscribe((response) => {
                const entries = Object.entries(response);
                const internal = entries.find(([key]) => key === "INTERNAL");
                const external = entries.find(([key]) => key === "EXTERNAL");
                // The address instant calculation lives on the Climate Change > Consolidated Risk
                // Score tab, so it has no dropdown view of its own.
                const rest = entries.filter(([key]) => key !== "INTERNAL" && key !== "EXTERNAL"
                    && key !== "CONSOLIDATED_RISK_SCORE");
                rest.sort((a, b) => a[1].localeCompare(b[1]));
                const finalSortedResponse: { [key: string]: string } = {};
                if (internal) finalSortedResponse[internal[0]] = internal[1];
                if (external) finalSortedResponse[external[0]] = external[1];
                for (const [key, value] of rest) {
                    finalSortedResponse[key] = this.transformDropdownLabel(key, value);
                }
                const result: [string, string][] = Object.entries(finalSortedResponse);
                this.menuList = result;
            if(this.isAddressLocationProfileCall()){
                this.checkModuleAccess();
            }
        });
            const component = componentsMapping.get(this.component);
            const factory = this.componentFactoryResolver.resolveComponentFactory(component);
            this.componentRef = this.container.createComponent(factory);

            if (this.componentConfigs) {
                Object.keys(this.componentConfigs).map((value: string) => {
                    this.componentRef.instance[value] = this.componentConfigs[value];
                });
            }

            this.show = true;
            this.isOpen = true;
            this.dragWidth = this.width;
            this.baseWidth = this.sidePanelService.getDefaultPanelWidthPx();
            this.minWidthPx = Math.round(this.baseWidth * 0.5);
            this.updateMaxWidth();
            this.dragWidth = this.clampWidth(this.dragWidth);
            this.applyChangeDetection();
        }
    }

    private checkModuleAccess() {
        let accountId = +this.route.snapshot.paramMap.get("accountId");
        this.tessadataService.checkAccountAccess(accountId)
            .subscribe((data) => {
                this.hasAccess = data;
                if (this.hasAccess.precisely_building) {
                    // The backend dropdown options now carry Precisely COPE too, so check
                    // before splicing or the address profile lists it twice.
                    const isPreciselyBuildingPresent = this.menuList.some(
                        (item) => JSON.stringify(item) === JSON.stringify(PRECISELY_BUILDING_ARRAY)
                            || (Array.isArray(item) && item[0] === PRECISELY_BUILDING_ARRAY[0])
                    );
                    if (!isPreciselyBuildingPresent) {
                        this.menuList.splice(5, 0, PRECISELY_BUILDING_ARRAY);
                    }
                }
                if (this.hasAccess.precisely_crime) {
                    const isPreciselyCrimePresent = this.menuList.some(
                        (item) => JSON.stringify(item) === JSON.stringify(PRECISELY_CRIME_ARRAY)
                    );
                    if (!isPreciselyCrimePresent) {
                        this.menuList.splice(6, 0, PRECISELY_CRIME_ARRAY);
                    }
                }
                if (this.hasAccess.precisely_wildfire_risk) {
                    const isPreciselyWildfireRiskPresent = this.menuList.some(
                        (item) => JSON.stringify(item) === JSON.stringify(PRECISELY_WILDFIRE_RISK_ARRAY)
                    );
                    if (!isPreciselyWildfireRiskPresent) {
                        this.menuList.splice(7, 0, PRECISELY_WILDFIRE_RISK_ARRAY);
                    }
                }
                if (this.hasAccess.munichre_hazards) {
                    const isMunichRePresent = this.menuList.some(
                        (item) => JSON.stringify(item) === JSON.stringify(MUNICHRE_ARRAY)
                    );
                    if (!isMunichRePresent) {
                        this.menuList.splice(2, 0, MUNICHRE_ARRAY);
                    }
                }
                if (this.hasAccess.munichre_report) {
                    const isMunichReReportPresent = this.menuList.some(
                        (item) => JSON.stringify(item) === JSON.stringify(MUNICHRE_REPORT_ARRAY)
                    );
                    if (!isMunichReReportPresent) {
                        this.menuList.splice(2, 0, MUNICHRE_REPORT_ARRAY);
                    }
                }
            });
    }

    showPanel() {
        if (this.show) {
            return; // Panel is already visible, don't reset width
        }
        this.show = true;
        this.isOpen = true;
        this.dragWidth = this.width;
        this.applyChangeDetection();
    }

    setPanelFirst() {
        this.onPanelFirst$.next(this);
    }

    closePanel(evt?: MouseEvent) {
        if (evt) evt.stopPropagation();
        this.show = false;
        this.isOpen = false;
        this.dragWidth = 0;
        this.closeModal$.next(true);
    }

    hidePanel(evt?: MouseEvent, doNotEmit?: boolean) {
        if (this.isAddressLocationProfileCall()) {
            this.selectedGroup.controls.defaultSelected.reset();
            this.selectedGroup.controls.defaultSelected.setValue('EXTERNAL');
          //  this.dataPointsServiceState.emitOnTensorflightAddressProfilePolygonFetch("");
            this.dataPointsServiceState.emitOnPreciselyAddressProfilePolygonFetch("");
            // Same reason as the Footprint polygon above: closing this panel only hides it, so the
            // address profile's ngOnDestroy never runs and its block group boundary would otherwise
            // stay drawn on the map.
            this.profilePolygonService.clearPreciselyCrimeBoundary();
            this.profilePolygonService.clearPreciselyWildfireRiskBoundary();
        }
        if (evt) evt.stopPropagation();
        this.show = false;
        this.isOpen = false;
        this.dragWidth = 0;
        if (!doNotEmit) this.hideModal$.next(this);
        this.workspaceItemService.setDashboardCall(false);
        this.applyChangeDetection();
    }

    onClick() {
        this.dataPointServiceState.emitDatapointUpdate(false);
    }

    applyChangeDetection() {
        this.changeDetectorRef.detectChanges();
    }

    get sizeClass() {
        return 'size-panel-' + this.size;
    }

    onMouseMove(event: MouseEvent) {
        if (!this.grabber) {
            return;
        }

        this.resizer(event.clientX - this.oldX);
        this.oldX = event.clientX;
        this.applyChangeDetection();
    }

    @HostListener('document:mouseup', ['$event'])
    onMouseUp(event: MouseEvent) {
        this.dragWidth = this.clampWidth(this.dragWidth);

        if (this.resizeable && this.dragWidth) {
            this.sidePanelService.setSessionPanelWidthPx(this.dragWidth);
        }

        this.grabber = false;
        this.subscription.unsubscribe();
    }

    resizer(offsetX: number) {
        const next = this.dragWidth - offsetX;
        this.dragWidth = this.clampWidth(next);
    }

    onMouseDown(event: MouseEvent) {
        this.grabber = true;
        this.oldX = event.clientX;

        this.subscription.unsubscribe();
        const mousemoveGlobal = fromEvent<MouseEvent>(document, 'mousemove');
        this.subscription = mousemoveGlobal
            .pipe(auditTime(0, animationFrameScheduler))
            .subscribe((e) => this.onMouseMove(e));
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
        this.destroy$.next(true);
        this.destroy$.complete();
    }

    selectionChange($event) {
        this.applyChangeDetection();
        this.selectedValue.setValue($event.value);
        this.dropdownValueChanged$.next($event.value);
        // Cleared before the emit, not after: the handlers raise it to 1 while they run, and
        // clearing afterwards left it at 1 for the next selection, which then skipped its fetch.
        this.dataPointServiceState.externalCallCount = 0;
        this.dataPointServiceState.emitOnExternalOverlaySelected($event.value);
    }

    isAddressLocationProfileCall() {
        return this.id === this.ADDRESS_LOCATION_PROFILE;
    }

    private transformDropdownLabel(key: string, value: string): string {
        return key === 'CLIMATE' ? 'Climate IPCC' : value;
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        this.updateMaxWidth();
        this.dragWidth = this.clampWidth(this.dragWidth);
        this.applyChangeDetection();
    }

    private updateMaxWidth(): void {
        this.maxWidthPx = Math.max(0, window.innerWidth - this.PANEL_MARGIN);
    }

    private clampWidth(value: number): number {
        return Math.min(Math.max(value, this.minWidthPx), this.maxWidthPx);
    }
}
